/**
 * Admin Help Content - SOP-style guidance for admin tools
 * 
 * Each entry provides:
 * - title: Tool name
 * - bullets: What it does & when to use it (2-4 lines)
 * - warnings: What to watch out for (risks)
 * - mistakes: Common mistakes to avoid
 * 
 * Used by: AdminHelp component
 */

export const ADMIN_HELP = {
  room_topics: {
    title: "Room Topics (Daily Topics)",
    bullets: [
      "Edit the daily topics shown inside each room (Her Space, Men's Work, Meditation, The Universe).",
      "Topics rotate automatically. Your changes apply immediately for all users.",
      "Use this to fix typos, swap themes, or refresh content without a code deploy."
    ],
    warnings: [
      "Changes affect all users in that room.",
      "Avoid deleting topics unless you replace them—empty days reduce engagement."
    ],
    mistakes: [
      "Editing the wrong room (double-check the room selector).",
      "Over-editing too frequently (keeps users from feeling a stable rhythm)."
    ]
  },

  special_date_topics: {
    title: "Special Date Topics (Holiday Overrides)",
    bullets: [
      "Override the normal daily topic for a specific date (e.g., Valentine's Day, Juneteenth).",
      "If a special topic exists for today's date, it will show instead of the normal rotation.",
      "Plan ahead for cultural moments: Pride, Black History Month, holidays, etc."
    ],
    warnings: [
      "Use your platform timezone consistently to avoid 'off by one day' issues.",
      "Special topics only apply to the exact date—they don't repeat annually."
    ],
    mistakes: [
      "Creating duplicates for the same room/date (use the existing entry instead).",
      "Forgetting to set 'active' to true before the date arrives."
    ]
  },

  payouts: {
    title: "Creator Payouts",
    bullets: [
      "Review withdrawal requests from users and approve or reject them.",
      "Approved payouts trigger a Stripe Connect transfer and log the transfer ID.",
      "Balances are ledger-based: pending vs available, holds, and reversals are enforced."
    ],
    warnings: [
      "Never approve payouts for unverified accounts.",
      "If chargebacks/refunds occur after payout, the ledger may show a negative balance—block further payouts until resolved."
    ],
    mistakes: [
      "Approving without reviewing history (always check account risk signals).",
      "Trying to 'fix' a user's balance manually instead of using reconciliation tools."
    ]
  },

  lovecoins: {
    title: "LoveCoins (Non-Cash)",
    bullets: [
      "LoveCoins are in-app, non-cash credits used for gifting and actions.",
      "They are NOT withdrawable and should never be treated like money.",
      "Use the reconciliation tool if balances look inconsistent."
    ],
    warnings: [
      "If balances look off, use the reconciliation tools—don't manually edit user fields.",
      "LoveCoins and Value Gifts are completely separate systems."
    ],
    mistakes: [
      "Confusing LoveCoins with cash earnings (value gifts / payouts).",
      "Manually adjusting user.lovecoins_balance instead of using ledger operations."
    ]
  },

  moderation_queue: {
    title: "Moderation Queue",
    bullets: [
      "Review reports and take action (warn, restrict, ban) based on platform policy.",
      "Use consistent outcomes to keep enforcement fair and predictable.",
      "Always add notes explaining your decision—future admins need context."
    ],
    warnings: [
      "Escalate repeated offenders and document actions with notes.",
      "Check message history for context before actioning."
    ],
    mistakes: [
      "Skipping notes—future admins need context to understand patterns.",
      "Applying inconsistent penalties for similar violations."
    ]
  },

  identity_verification: {
    title: "Identity Verification",
    bullets: [
      "Review verification status and ensure high-risk actions are gated behind verification.",
      "Verification is required for payouts and other trust-sensitive features.",
      "Check both ID status and profile consistency."
    ],
    warnings: [
      "Do not override verification requirements unless policy explicitly allows it.",
      "Report suspicious verification attempts to security."
    ],
    mistakes: [
      "Approving payouts for unverified users.",
      "Ignoring verification mismatch flags."
    ]
  },

  analytics: {
    title: "Analytics (What the numbers mean)",
    bullets: [
      "Use metrics to monitor retention, revenue health, and safety pressure.",
      "Look for trends (week over week), not single-day spikes.",
      "Key metrics: DAU, MAU, retention rate, ARPU, churn, refund rate."
    ],
    warnings: [
      "Refund/chargeback spikes may indicate abuse or onboarding issues.",
      "Single-day dips are often noise—look at 7-day moving averages."
    ],
    mistakes: [
      "Overreacting to a single day of data.",
      "Comparing weekday vs weekend metrics without accounting for patterns."
    ]
  },

  system_build_info: {
    title: "Build Information",
    bullets: [
      "Confirms which deployment you're currently viewing (timestamp + commit hash).",
      "Use this when troubleshooting: it prevents 'stale build' confusion.",
      "The build fingerprint updates on every deployment."
    ],
    warnings: [
      "If a reported bug can't be reproduced, verify build stamp first.",
      "Hard refresh (Ctrl+Shift+R) clears browser cache if you see stale code."
    ],
    mistakes: [
      "Debugging without confirming environment/build.",
      "Assuming the user sees the same build you do."
    ]
  },

  payout_metrics: {
    title: "Payout Analytics",
    bullets: [
      "Shows health of the payout system: total paid, average amount, approval speed.",
      "Pending count shows how many requests are waiting for admin action.",
      "Look at trends over time, not single data points."
    ],
    warnings: [
      "A spike in pending requests may indicate approval bottleneck.",
      "Very fast approval times could mean inadequate review."
    ],
    mistakes: [
      "Ignoring a growing pending queue (leads to creator frustration).",
      "Assuming low numbers mean no issues (check for eligibility blockers)."
    ]
  },

  value_payouts: {
    title: "Value Payouts (Appreciation Gifts)",
    bullets: [
      "Users earn real USD value when they receive appreciation gifts.",
      "Value is held for 30 days before becoming withdrawable (fraud protection).",
      "This is separate from LoveCoins—Value Gifts have real monetary value."
    ],
    warnings: [
      "Watch for suspicious gifting patterns (self-gifting, coordinated schemes).",
      "Reversals are possible if the original gift payment is refunded/charged back."
    ],
    mistakes: [
      "Confusing Value Gifts with LoveCoins (they're separate systems).",
      "Approving payouts without checking if the underlying gifts are legitimate."
    ]
  },

  admin_dashboard: {
    title: "Admin Dashboard Overview",
    bullets: [
      "Central hub for monitoring platform health, revenue, and safety.",
      "Quick-links take you to specific admin tools.",
      "Build info at the bottom confirms your current deployment version."
    ],
    warnings: [
      "Check analytics for unusual patterns before diving into specific tools.",
      "Use the refresh button to get the latest data."
    ],
    mistakes: [
      "Taking action without reviewing the full context across tools.",
      "Assuming cached data is current (always refresh for critical decisions)."
    ]
  },

  feature_flags: {
    title: "Feature Flags",
    bullets: [
      "Toggle features on/off without code deploys.",
      "Used for gradual rollouts, A/B tests, and emergency shutoffs.",
      "Changes take effect immediately—no restart required."
    ],
    warnings: [
      "Test flag changes in dev/staging before production when possible.",
      "Some flags have dependencies—disabling one may break others."
    ],
    mistakes: [
      "Forgetting to re-enable a flag after testing.",
      "Changing flags during peak hours without preparation."
    ]
  },

  user_360: {
    title: "User 360 (Full Profile View)",
    bullets: [
      "See everything about a user: profile, matches, messages, payments, reports.",
      "Use this for investigating issues, support tickets, or suspicious activity.",
      "All actions on this page are logged for audit."
    ],
    warnings: [
      "Access is logged—only view users you have legitimate reason to investigate.",
      "Don't share user data outside of authorized support channels."
    ],
    mistakes: [
      "Making changes without documenting the reason.",
      "Looking up users out of curiosity (this is audited)."
    ]
  },

  bulk_import: {
    title: "Bulk Import (Calendar Seeder)",
    bullets: [
      "Upload months or years of special topics at once using JSON.",
      "Preload cultural calendars: Black History Month, Pride, Juneteenth, Kwanzaa, etc.",
      "Existing dates will be updated automatically—invalid rows are skipped."
    ],
    warnings: [
      "Review the preview before confirming import.",
      "Invalid room slugs will fail (check room names match exactly)."
    ],
    mistakes: [
      "Uploading without previewing—always check the first 5 rows.",
      "Using wrong date format (must be YYYY-MM-DD)."
    ]
  },

  batch_payouts: {
    title: "Batch Payout Actions",
    bullets: [
      "Approve or reject multiple payout requests at once (10-50+ at a time).",
      "Saves admin time when processing payout queues.",
      "Already-paid requests are safely skipped (idempotent)."
    ],
    warnings: [
      "Batch approve sends real money—verify eligibility before confirming.",
      "Failed items remain selected for retry."
    ],
    mistakes: [
      "Approving without reviewing individual amounts or patterns.",
      "Ignoring partial failures—check the results summary."
    ]
  },

  her_space_private: {
    title: "Her Space (Private Room)",
    bullets: [
      "This room is accessible only through the Her Space hub.",
      "Women-only access enforced server-side (men receive 403).",
      "The room does NOT appear in the main Rooms list."
    ],
    warnings: [
      "Gender gate is enforced on the backend—it cannot be bypassed from frontend.",
      "Users must update their profile gender to gain access."
    ],
    mistakes: [
      "Trying to add Her Space Private to the public rooms list (it's hidden by design).",
      "Assuming manual overrides are possible (gender check is server-side)."
    ]
  }
};

export default ADMIN_HELP;
